import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router';
import { z } from 'zod';
import { ApiError } from '../api/client';
import {
  type PatchStripeAccountBody,
  useStripeAccount,
  usePatchStripeAccount,
} from '../api/stripeAccounts';
import { Field } from '../components/Field';

const SLUG_RE = /^[a-z0-9-]+$/;
const SK_RE = /^sk_(test|live)_[A-Za-z0-9]+$/;
const WHSEC_RE = /^whsec_[A-Za-z0-9]+$/;
const PK_RE = /^pk_(test|live)_[A-Za-z0-9]+$/;
const ACCT_RE = /^acct_[A-Za-z0-9]+$/;

const editSchema = z.object({
  label: z.string().min(1).max(64).regex(SLUG_RE),
  displayName: z.string().min(1).max(120),
  publishableKey: z.string().regex(PK_RE),
  stripeAccountId: z
    .string()
    .refine((s) => s === '' || ACCT_RE.test(s), { message: 'must match acct_xxx' }),
  // Empty string means "leave alone" in this form. The backend's PATCH
  // contract is "field omitted = no change"; we omit empty strings before
  // submit so the backend never sees them.
  newSecretKey: z
    .string()
    .refine((s) => s === '' || SK_RE.test(s), { message: 'sk_test_… or sk_live_…' }),
  newWebhookSecret: z
    .string()
    .refine((s) => s === '' || WHSEC_RE.test(s), { message: 'whsec_…' }),
});

type EditValues = z.infer<typeof editSchema>;

const ERROR_LABELS: Record<string, string> = {
  stripe_auth_failed: 'Stripe rejected the new secret key (authentication failed).',
  stripe_permission_denied: 'The new secret key does not have the required permissions.',
  stripe_unreachable: "Couldn't reach Stripe. Try again.",
  stripe_rate_limited: 'Stripe rate-limited the request. Wait a minute and retry.',
  stripe_account_mismatch:
    "The new secret key doesn't belong to the Stripe account id on this row.",
  stripe_unknown: 'Stripe returned an unexpected error.',
  label_taken: 'A Stripe account with that label already exists.',
};

function formatUpdated(at: string | null, by: string | null): string {
  if (!at) return 'never';
  const when = new Date(at).toLocaleString();
  return by === null ? `${when} (CLI)` : when;
}

export function StripeAccountEdit() {
  const { accountId = '' } = useParams();
  const accountQuery = useStripeAccount(accountId);
  const patchAccount = usePatchStripeAccount(accountId);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [showReplaceSk, setShowReplaceSk] = useState(false);
  const [showReplaceWh, setShowReplaceWh] = useState(false);

  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      label: '',
      displayName: '',
      publishableKey: '',
      stripeAccountId: '',
      newSecretKey: '',
      newWebhookSecret: '',
    },
  });

  useEffect(() => {
    if (accountQuery.data) {
      form.reset({
        label: accountQuery.data.label,
        displayName: accountQuery.data.displayName,
        publishableKey: accountQuery.data.publishableKey ?? '',
        stripeAccountId: accountQuery.data.stripeAccountId ?? '',
        newSecretKey: '',
        newWebhookSecret: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountQuery.data?.id]);

  useEffect(() => {
    if (savedAt === null) return;
    const t = setTimeout(() => setSavedAt(null), 2500);
    return () => clearTimeout(t);
  }, [savedAt]);

  if (accountQuery.isPending) return <div className="loading">Loading…</div>;
  if (!accountQuery.data) return <div className="error">Could not load Stripe account.</div>;
  const account = accountQuery.data;

  const onSubmit = form.handleSubmit(async (values) => {
    form.clearErrors('root');
    const body: PatchStripeAccountBody = {};
    if (values.label !== account.label) body.label = values.label;
    if (values.displayName !== account.displayName) body.displayName = values.displayName;
    if (values.publishableKey !== (account.publishableKey ?? '')) {
      body.publishableKey = values.publishableKey;
    }
    if (values.stripeAccountId !== (account.stripeAccountId ?? '')) {
      body.stripeAccountId = values.stripeAccountId;
    }
    if (values.newSecretKey !== '') body.secretKey = values.newSecretKey;
    if (values.newWebhookSecret !== '') body.webhookSecret = values.newWebhookSecret;
    if (Object.keys(body).length === 0) {
      setSavedAt(Date.now()); // nothing to do, but the user clicked save
      return;
    }
    try {
      await patchAccount.mutateAsync(body);
      setSavedAt(Date.now());
      setShowReplaceSk(false);
      setShowReplaceWh(false);
      form.setValue('newSecretKey', '');
      form.setValue('newWebhookSecret', '');
    } catch (err) {
      if (err instanceof ApiError) {
        form.setError('root', {
          message: ERROR_LABELS[err.code] ?? 'Could not save changes.',
        });
      } else {
        form.setError('root', { message: "Couldn't reach the server." });
      }
    }
  });

  return (
    <div className="page">
      <div className="page-head">
        <h1>Edit Stripe account</h1>
        <div className="page-actions">
          <Link to="/stripe-accounts" className="btn btn-ghost">
            Back
          </Link>
        </div>
      </div>
      <form onSubmit={onSubmit} className="card card-pad">
        <Field label="Display name" error={form.formState.errors.displayName?.message}>
          <input type="text" autoComplete="off" {...form.register('displayName')} />
        </Field>
        <Field label="Label (slug)" error={form.formState.errors.label?.message}>
          <input type="text" autoComplete="off" {...form.register('label')} />
        </Field>
        <Field
          label="Stripe account id"
          help="Empty clears the value back to null."
          error={form.formState.errors.stripeAccountId?.message}
        >
          <input type="text" autoComplete="off" {...form.register('stripeAccountId')} />
        </Field>
        <Field
          label="Publishable key"
          error={form.formState.errors.publishableKey?.message}
        >
          <input type="text" autoComplete="off" {...form.register('publishableKey')} />
        </Field>

        <Field label="Secret key">
          {account.secretKey.set ? (
            <div>
              <span>
                Set · last 4 …{account.secretKey.last4 ?? '????'} ·{' '}
                <span className="micro">
                  updated {formatUpdated(account.secretKey.updatedAt, account.secretKey.updatedBy)}
                </span>
              </span>
              {!showReplaceSk && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginLeft: 12 }}
                  onClick={() => setShowReplaceSk(true)}
                >
                  Replace
                </button>
              )}
            </div>
          ) : (
            <div className="micro">Not set</div>
          )}
          {(showReplaceSk || !account.secretKey.set) && (
            <div style={{ marginTop: 8 }}>
              <input
                type="password"
                autoComplete="off"
                placeholder="sk_test_… or sk_live_…"
                {...form.register('newSecretKey')}
              />
              {form.formState.errors.newSecretKey?.message && (
                <div className="field-error">{form.formState.errors.newSecretKey.message}</div>
              )}
              {showReplaceSk && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: 6 }}
                  onClick={() => {
                    setShowReplaceSk(false);
                    form.setValue('newSecretKey', '');
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </Field>

        <Field label="Webhook signing secret">
          {account.webhookSecret.set ? (
            <div>
              <span>
                Set · last 4 …{account.webhookSecret.last4 ?? '????'} ·{' '}
                <span className="micro">
                  updated{' '}
                  {formatUpdated(
                    account.webhookSecret.updatedAt,
                    account.webhookSecret.updatedBy,
                  )}
                </span>
              </span>
              {!showReplaceWh && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginLeft: 12 }}
                  onClick={() => setShowReplaceWh(true)}
                >
                  Replace
                </button>
              )}
            </div>
          ) : (
            <div className="micro">Not set</div>
          )}
          {(showReplaceWh || !account.webhookSecret.set) && (
            <div style={{ marginTop: 8 }}>
              <input
                type="password"
                autoComplete="off"
                placeholder="whsec_…"
                {...form.register('newWebhookSecret')}
              />
              {form.formState.errors.newWebhookSecret?.message && (
                <div className="field-error">
                  {form.formState.errors.newWebhookSecret.message}
                </div>
              )}
              {showReplaceWh && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: 6 }}
                  onClick={() => {
                    setShowReplaceWh(false);
                    form.setValue('newWebhookSecret', '');
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </Field>

        {form.formState.errors.root && (
          <div className="form-error">{form.formState.errors.root.message}</div>
        )}
        <div className="page-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
          {savedAt !== null && <span className="micro">Saved.</span>}
        </div>
      </form>
    </div>
  );
}
